import type { Profile as ProfileData } from "../../../api/types";
import { Progress } from "../../components/Progress";
import { cx } from "../../cx";
import { formatInt, formatPercent, formatPlaytime, formatRatio } from "../../format";
import { Lcd } from "./Lcd";
import { headshotRate, kdRatio, killsPerMatch, winRate } from "./profileStats";

interface Stat {
  label: string;
  value: string;
  /** Share 0..1 drawn as a thin LED strip along the bottom of the line (null: nothing to show yet). */
  share?: number | null;
  good?: boolean;
}

/** Lifetime stat lines (LCD readouts). The grid has fixed row heights, so values changing never move anything. */
export function StatsGrid({ profile }: { profile: ProfileData }) {
  const kd = kdRatio(profile.kills, profile.deaths);
  const perMatch = killsPerMatch(profile.kills, profile.matches);
  const stats: Stat[] = [
    { label: "Matches", value: formatInt(profile.matches) },
    { label: "Wins", value: formatInt(profile.wins) },
    {
      label: "Win rate",
      value: formatPercent(profile.wins, profile.matches),
      share: winRate(profile.wins, profile.matches),
    },
    { label: "Playtime", value: formatPlaytime(profile.playtimeSec) },
    { label: "Kills / match", value: perMatch === null ? "-" : perMatch.toFixed(1) },
    { label: "Kills", value: formatInt(profile.kills) },
    { label: "Deaths", value: formatInt(profile.deaths) },
    { label: "Assists", value: formatInt(profile.assists) },
    { label: "K/D", value: formatRatio(profile.kills, profile.deaths), good: kd !== null && kd >= 1 },
    {
      label: "Headshot %",
      value: formatPercent(profile.headshots, profile.kills),
      share: headshotRate(profile.headshots, profile.kills),
    },
  ];

  return (
    <section class="profile__panel profile__panel--stats" aria-labelledby="profile-stats-title">
      <header class="profile__panel-head">
        <h2 id="profile-stats-title" class="profile__h">
          Lifetime stats
        </h2>
      </header>
      <dl class="profile__stats">
        {stats.map((stat) => (
          <div key={stat.label} class={cx("profile__stat", stat.good && "is-good")}>
            <dt>{stat.label}</dt>
            <dd>
              <Lcd text={stat.value} />
            </dd>
            {stat.share !== undefined && (
              <Progress value={stat.share ?? 0} max={1} label={stat.label} class="profile__stat-meter" />
            )}
          </div>
        ))}
      </dl>
    </section>
  );
}
